import { type FocusEvent, useLayoutEffect, useRef } from "react";

type Props = {
  defaultValue: string;
  onBlur: (e: FocusEvent<HTMLTextAreaElement>) => void;
};

const fitHeight = (textarea: HTMLTextAreaElement) => {
  // auto に戻した瞬間にページが短くなり、下端付近ではブラウザがスクロール位置を詰めてしまう。
  // この画面は window ではなく body がスクロールするので、祖先の要素ごとに位置を控えて戻す
  const scrollPositions: [Element, number][] = [];
  for (let el = textarea.parentElement; el; el = el.parentElement) {
    if (el.scrollTop > 0) scrollPositions.push([el, el.scrollTop]);
  }
  // 一度 auto に戻さないと、内容が減ったときに scrollHeight が今の高さのまま縮まない
  textarea.style.height = "auto";
  const borderHeight = textarea.offsetHeight - textarea.clientHeight;
  textarea.style.height = `${textarea.scrollHeight + borderHeight}px`;
  for (const [el, scrollTop] of scrollPositions) {
    if (el.scrollTop !== scrollTop) el.scrollTop = scrollTop;
  }
};

export const MemoTextarea = ({ defaultValue, onBlur }: Props) => {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const textarea = ref.current;
    if (!textarea) return;
    fitHeight(textarea);

    // ここで設定した高さの変化にも通知が来る。
    // 横幅が同じなら折り返しは変わらないので、合わせ直さずに繰り返しを断つ
    let lastWidth = textarea.clientWidth;
    const observer = new ResizeObserver(() => {
      if (textarea.clientWidth === lastWidth) return;
      lastWidth = textarea.clientWidth;
      fitHeight(textarea);
    });
    observer.observe(textarea);
    return () => observer.disconnect();
  }, []);

  return (
    <textarea
      ref={ref}
      className="box-border w-full resize-none overflow-hidden rounded border border-[var(--color-border-default)] bg-white p-2 text-sm outline-none focus:border-[var(--color-primary-500)]"
      defaultValue={defaultValue}
      onInput={(e) => fitHeight(e.currentTarget)}
      onBlur={onBlur}
      placeholder="メモを入力..."
      rows={3}
    />
  );
};
